import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import PageFilterBar from 'src/component/v2/PageFilterBar';
import DeviceList from 'src/component/v2/DeviceList/DeviceList';
import DeviceFilterChips from 'src/component/v2/DeviceList/DeviceFilterChips';
import DeviceListSummary from 'src/component/v2/DeviceList/DeviceListSummary';
import DeviceListToolbar from 'src/component/v2/DeviceList/DeviceListToolbar';
import useDeviceListSearch from 'src/component/v2/DeviceList/useDeviceListSearch';
import DeviceNav from 'src/component/v2/DeviceNav/DeviceNav';
import useDeviceNavSelection from 'src/component/v2/DeviceNav/useDeviceNavSelection';
import { columnsForSelection, toDeviceListScope } from 'src/component/v2/DeviceNav/deviceNavState';
import { cn } from 'src/component/ds/utils';
import { getSelectedUnit } from 'src/module/filter/selector';
import { useTranslation } from 'src/i18n';
import { isWhitelabel } from 'src/utils/utils';

/* Reserves space for a part of the design that isn't built yet. Labels are for developers only. */
const Placeholder = ({ label, className }: { label: string; className?: string }) => (
  <div
    aria-hidden='true'
    className={cn(
      'ds-flex ds-items-center ds-justify-center ds-rounded ds-border ds-border-dashed ds-border-border',
      'ds-text-xs ds-text-muted-foreground',
      className
    )}
  >
    {label}
  </div>
);

export default function GroupedDevicesScreen () {
  const { t } = useTranslation();
  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';
  const [selection, setSelection] = useDeviceNavSelection();
  const [search, setSearch] = useDeviceListSearch();
  const scope = useMemo(() => toDeviceListScope(selection), [selection]);
  const selectedUnit = useSelector(getSelectedUnit);
  const columns = columnsForSelection(selection, Boolean(selectedUnit));

  return (
    <div className='ds-flex ds-h-full ds-flex-col ds-bg-background'>
      <Helmet>
        <title>{title} - {t('deviceList.title')}</title>
      </Helmet>

      <h1 className='ds-sr-only'>{t('deviceList.title')}</h1>

      <PageFilterBar />

      <div className='ds-flex ds-min-h-0 ds-flex-1 ds-gap-8 ds-px-4 ds-pt-4'>
        <DeviceNav selection={selection} onSelect={setSelection} className='ds-mb-4' />

        <div className='ds-flex ds-min-w-0 ds-flex-1 ds-flex-col ds-gap-8'>
          <DeviceListToolbar search={search} onSearchChange={setSearch} className='ds-pt-4' />
          <div className='ds-flex ds-flex-col ds-gap-1'>
            <DeviceFilterChips />
            <DeviceListSummary />
          </div>

          <DeviceList
            scope={scope}
            search={search}
            columns={columns}
            onViewAllDevices={() => setSelection({ tab: 'panels' })}
            className='ds-min-h-0 ds-flex-1'
          />
        </div>
      </div>
    </div>
  );
}
